import './Loading.css';
import './LoadingButton.css';

import classnames from 'classnames';
import React, { memo, useCallback, useState } from 'react';

import { useMounted } from 'Hooks';

import * as log from 'Utilities/log';

// https://loading.io/button/
// For example of use look at messaging/MessageThread.js

type Props = {
  onClick?: (event: React.MouseEvent) => Promise<any> | undefined | void;
  running?: boolean;
} & (React.AnchorHTMLAttributes<HTMLAnchorElement> | React.ButtonHTMLAttributes<HTMLButtonElement>);

/**
 * This is a button that can be used to show loading state when performing something asynchronously
 * You can achieve this by either passing in an onClick that returns a promise or by passing in
 * the 'running' prop flag.
 * https://loading.io/button/
 *
 * Optional Props:
 *
 * - type - defaults to 'button' - allows you to set the type of the button
 *
 * - children - the content to render inside the button
 *
 * - onClick - the method to execute when clicked. This method needs to return a promise.
 * The promise is used to determine if the loading state should be shown
 *
 * - running - a flag to determine if you want the button to show that something async is happening
 *
 * - className - defaults to 'btn btn-primary' - classes to be applied to the button
 *
 * - other props - will be passed on to the button element
 */
const LoadingButton: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
  id = 'loadingButton-button',
  onClick,
  running,
  ...other
}) => {
  const mounted = useMounted();
  const [css, setCss] = useState('');

  const clickWrapper = useCallback(
    (event: React.MouseEvent) => {
      if (!onClick) {
        return;
      }

      if (css === '') {
        try {
          const result = onClick(event);

          if (result && typeof result.then === 'function') {
            setCss('running');
            // we have a promise
            result.finally(() => {
              if (mounted.current) {
                setCss('');
              }
            });
          }
        } catch (e) {
          log.error(e);
          setCss('');
        }
      }
    },
    [css, mounted, onClick]
  );

  const buttonClass = classnames(className || 'btn btn-primary', 'ld-over', css, {
    running: running === true,
  });

  if ('href' in other) {
    return (
      <a id={id} className={buttonClass} href={other.href} onClick={clickWrapper} {...other}>
        {children}
        <div id={`${id}-spinner`} className="ld ld-ring ld-spin" />
      </a>
    );
  } else {
    return (
      <button
        id={id}
        className={buttonClass}
        onClick={clickWrapper}
        type="button"
        {...(other as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {children}
        <div id={`${id}-spinner`} className="ld ld-ring ld-spin" />
      </button>
    );
  }
};

export default memo(LoadingButton);
